import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Potegni za osvežitev.
 *
 * Stara verzija je useEffect vezala na `pullDistance`, ki se je spreminjal
 * ob vsakem premiku prsta — torej je ob vsakem okvirju odstranila in znova
 * dodala tri poslušalce dogodkov. Tu se poslušalci dodajo enkrat, trenutno
 * stanje pa živi v ref-ih.
 */
export function usePullToRefresh(onRefresh, enabled = true) {
  const [distance, setDistance] = useState(0);
  const [busy, setBusy] = useState(false);

  const startY = useRef(0);
  const pulling = useRef(false);
  const distRef = useRef(0);
  const busyRef = useRef(false);
  const cb = useRef(onRefresh);
  cb.current = onRefresh;

  const set = useCallback((d) => {
    distRef.current = d;
    setDistance(d);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onStart = (e) => {
      if (window.scrollY > 0 || busyRef.current) {
        pulling.current = false;
        return;
      }
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onMove = (e) => {
      if (!pulling.current || busyRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      // Upor: bolj kot vlečeš, počasneje sledi. Tako se konec poteka konča mehko.
      set(dy > 0 && window.scrollY === 0 ? Math.min(dy * 0.45, 88) : 0);
    };

    const onEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (distRef.current > 60) {
        busyRef.current = true;
        setBusy(true);
        set(56);
        try {
          await cb.current?.();
        } finally {
          busyRef.current = false;
          setBusy(false);
          set(0);
        }
      } else {
        set(0);
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, set]);

  return { distance, busy };
}

/**
 * Tema: SVETLA, razen če uporabnik izrecno izbere temno.
 *
 * Aplikacija NE sledi napravi. Uporablja se na vzletišču, na soncu, kjer je
 * svetla podlaga berljivejša — in tečajnik, ki ima telefon v temnem načinu,
 * si tega za to aplikacijo ni nujno želel.
 *
 * Ključ je namenoma nov (`-v2`). Prejšnja različica je v napravo shranila
 * `system` in ta shranjena vrednost je premagala novo privzeto nastavitev,
 * zato je app na temni napravi ostal črn. Nov ključ staro vrednost preprosto
 * prezre.
 */
const THEME_KEY = 'perut-theme-v2';
const DEFAULT_THEME = 'light';

const readTheme = () => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' ? 'dark' : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

export function useTheme() {
  const [theme, setThemeState] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ni ključno */
    }
  }, [theme]);

  return [theme, setThemeState];
}

/*
  Namestitev PWA. Chrome ponudi `beforeinstallprompt`, iOS Safari ne —
  tam pokažemo navodila.

  Ko aplikacijo enkrat odpreš nameščeno, si to zapomnimo. Sicer bi ji, ko
  jo kdaj odpreš še v brskalniku, spet ponujala namestitev — čeprav jo ima
  uporabnik že na zaslonu. Na iOS-u tega ni mogoče ugotoviti drugače:
  `beforeinstallprompt` tam nikoli ne pride.
*/
const INSTALLED_KEY = 'perut-installed';

const rememberInstalled = () => {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
  } catch {
    /* ni ključno */
  }
};

const wasInstalled = () => {
  try {
    return localStorage.getItem(INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
};

export function useInstall() {
  const [prompt, setPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [known, setKnown] = useState(wasInstalled);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setStandalone(isStandalone);
    if (isStandalone) {
      rememberInstalled();
      setKnown(true);
    }

    const onBIP = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => {
      setPrompt(null);
      setStandalone(true);
      rememberInstalled();
      setKnown(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return false; // klicatelj naj pokaže navodila
    prompt.prompt();
    try {
      await prompt.userChoice;
    } catch {
      /* uporabnik je zaprl */
    }
    setPrompt(null);
    return true;
  }, [prompt]);

  return { canPrompt: !!prompt, standalone, alreadyInstalled: known, install };
}

/** Ali je uporabnik brez povezave. */
export function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/** Vrednost, ki zaostaja za vnosom — da iskanje ne filtrira ob vsaki tipki. */
export function useDebounced(value, ms = 200) {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
}

/*
  Gumb „nazaj" na napravi zapre plast, ne aplikacije.

  Aplikacija nima poti (URL se ne spreminja), zato je na telefonu pritisk na
  sistemski gumb za nazaj ali poteg z roba zaprl celo aplikacijo — tudi kadar
  je bil odprt samo modal ali detajl tečajnika. V nameščeni PWA je to pomenilo
  izhod iz appa.

  Kako deluje: zgodovine se NE dotikamo ob vsaki plasti. Ko se odpre prva
  plast, dodamo en sam „stražarski" vnos in začnemo poslušati popstate. Ko
  uporabnik pritisne nazaj, zapremo najvišjo plast in stražarja takoj vrnemo.
  Ko se zapre zadnja plast, stražarja poberemo.

  Zakaj tako in ne vnos na plast: React v razvoju učinke požene dvakrat
  (mount → cleanup → mount). Pri vnosu na plast je čiščenje sprožilo
  history.back(), ta pa je asinhrono prišel šele po drugem mountu in plast
  takoj zaprl. Odstranjevanje stražarja je zato zamaknjeno za en tick in se
  prekliče, če se plast v istem tiku znova odpre.
*/

const backLayers = [];
let backArmed = false;
let disarmTimer = null;

function onBackPop() {
  const top = backLayers[backLayers.length - 1];
  if (top) {
    top();
    // Stražarja vrnemo, da naslednji „nazaj" zapre naslednjo plast.
    window.history.pushState({ perutBack: true }, '');
  } else {
    backArmed = false;
    window.removeEventListener('popstate', onBackPop);
  }
}

function armBack() {
  clearTimeout(disarmTimer);
  disarmTimer = null;
  if (backArmed) return;
  backArmed = true;
  window.history.pushState({ perutBack: true }, '');
  window.addEventListener('popstate', onBackPop);
}

function scheduleDisarm() {
  if (disarmTimer) return;
  disarmTimer = setTimeout(() => {
    disarmTimer = null;
    if (backLayers.length || !backArmed) return;
    backArmed = false;
    window.removeEventListener('popstate', onBackPop);
    if (window.history.state?.perutBack) window.history.back();
  }, 0);
}

export function useBackDismiss(active, dismiss) {
  const cb = useRef(dismiss);
  cb.current = dismiss;

  useEffect(() => {
    if (!active) return undefined;

    const layer = () => cb.current?.();
    backLayers.push(layer);
    armBack();

    return () => {
      const i = backLayers.lastIndexOf(layer);
      if (i >= 0) backLayers.splice(i, 1);
      if (backLayers.length === 0) scheduleDisarm();
    };
  }, [active]);
}

/**
 * Kratek drget ob potrditvi dejanja.
 *
 * Na Androidu deluje prek Vibration API, na iOS-u v brskalniku ne obstaja —
 * tam preprosto ne naredi nič. Zato ga uporabljaj samo kot dodatek k vidni
 * povratni informaciji, nikoli kot edino.
 */
export function haptic(pattern = 12) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* naprava tega ne podpira */
  }
}
